import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Spinner,
  Table,
  UncontrolledDropdown,
} from "reactstrap";

// react table
import {
  Table as ReactTable,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

// redux toolkit
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { fetchOrders } from "../../slices/apps/orderSlice";

// sayfalar, modallar vb.
import DebouncedInput from "../../Components/Common/DebouncedInput";
import TablePagination from "../../Components/Common/TablePagination";
import AddModal from "./AddModal";
import UpdateModal from "./UpdateModal";
import DeleteModal from "./DeleteModal";

// Column Definitions
const columnHelper = createColumnHelper();

const pageSizeOptions = [10, 25, 50, 100, 200];

const currencyFormatter = new Intl.NumberFormat("tr-TR", {
  style: "currency",
  currency: "TRY",
});

const OrderTable = () => {
  const dispatch = useDispatch();

  const { orders, ordersLoading, isUserData } = useSelector((state) => {
    return {
      orders: state.order.orders,
      ordersLoading: state.order.ordersLoading,
      isUserData: state.auth.isUserData,
    };
  }, shallowEqual);

  // States
  const [data, setData] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [filterStatus, setFilterStatus] = useState("");

  const [selectedRowData, setSelectedRowData] = useState(null);
  const [modalState, setModalState] = useState({
    update: false,
    delete: false,
  });

  const toggleModal = (modalName, rowData = null) => {
    setSelectedRowData(rowData);
    setModalState((prevState) => ({
      ...prevState,
      [modalName]: !prevState[modalName],
    }));
  };

  useEffect(() => {
    if (!orders) {
      setData([]);
      return;
    }

    const filteredData = orders?.filter((data) => {
      if (filterStatus && data.isActive.toString() != filterStatus)
        return false;

      if (globalFilter) {
        const searchTerm = globalFilter.toLocaleLowerCase("tr");
        const searchableFields = [
          data.orderNo,
          data.totalAmount,
          data.customerName,
          data.companyName,
        ];
        const match = searchableFields.some(
          (field) =>
            field &&
            field.toString().toLocaleLowerCase("tr").includes(searchTerm)
        );
        if (!match) return false;
      }

      return true;
    });

    setData(filteredData);
  }, [globalFilter, filterStatus, orders]);

  // Hooks
  const columns = useMemo(
    () => [
      columnHelper.accessor("actions", {
        header: "",
        size: 46,
        minSize: 46,
        maxSize: 46,
        enableSorting: false,
        cell: ({ row }) => (
          <UncontrolledDropdown>
            <DropdownToggle
              tag="button"
              className="btn btn-sm btn-ghost-primary btn-icon"
            >
              <i className="ri-more-2-fill fs-16"></i>
            </DropdownToggle>
            <DropdownMenu className="dropdown-menu-end" strategy="fixed">
              <DropdownItem onClick={() => toggleModal("update", row.original)}>
                <i className="ri-settings-3-line align-middle me-1"></i>
                Düzenle
              </DropdownItem>
              {isUserData.role == "Admin" && (
                <DropdownItem
                  onClick={() => toggleModal("delete", row.original)}
                >
                  <i className="ri-delete-bin-line align-middle me-1"></i>Sil
                </DropdownItem>
              )}
            </DropdownMenu>
          </UncontrolledDropdown>
        ),
      }),

      columnHelper.accessor("orderNo", {
        header: "Servis No",
        size: 110,
        minSize: 110,
        maxSize: 110,
        enableResizing: false,

        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {row.original.orderNo}
          </div>
        ),
      }),

      columnHelper.accessor("customerName", {
        header: "Müşteri Adı Soyadı",
        size: "auto",
        minSize: 150,
        maxSize: 250,
        enableResizing: false,

        cell: ({ row }) => (
          <div className="text-truncate fw-bold fs-12">
            {row.original.customerName ? row.original.customerName : "-"}
            {row.original.companyName && (
              <p className="text-muted fw-semibold mb-0 small">
                {row.original.companyName}
              </p>
            )}
          </div>
        ),
      }),

      columnHelper.accessor("couponAmount", {
        header: "Kupon Tutarı",
        size: 150,
        minSize: 150,
        maxSize: 150,
        enableResizing: false,

        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {row.original.couponAmount
              ? currencyFormatter.format(row.original.couponAmount)
              : "₺0,00"}
          </div>
        ),
      }),

      columnHelper.accessor("commissionFee", {
        header: "Komisyon Tutarı",
        size: 150,
        minSize: 150,
        maxSize: 150,
        enableResizing: false,

        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {currencyFormatter.format(row.original.commissionFee)}
          </div>
        ),
      }),

      columnHelper.accessor("totalAmount", {
        header: "Toplam Tutar",
        size: 150,
        minSize: 150,
        maxSize: 150,
        enableResizing: false,

        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {currencyFormatter.format(row.original.totalAmount)}
          </div>
        ),
      }),

      columnHelper.accessor("creationDate", {
        header: "Servis Tarihi",
        size: 150,
        minSize: 150,
        maxSize: 150,
        enableResizing: false,
        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {new Date(row.original.creationDate).toLocaleString("tr-TR")}
          </div>
        ),
      }),
    ],
    [isUserData]
  );

  // Hooks
  const table = useReactTable({
    data: data,
    columns,
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const { getHeaderGroups, getRowModel, setPageSize, getState } = table;

  const refreshData = async () => {
    try {
      await dispatch(fetchOrders()).unwrap();
    } catch (error) { /* Silently handled */ }
  };

  return (
    <Fragment>
      <Card>
        <CardBody className="d-flex align-items-center gap-2">
          <Button
            className="btn btn-label btn"
            color="warning"
            type="button"
            onClick={() => refreshData()}
          >
            <i className="ri-refresh-line label-icon align-middle me-2"></i>
            Yenile
          </Button>
          <AddModal />
        </CardBody>
      </Card>
      <Card>
        <CardHeader className="align-items-center d-flex">
          <h4 className="card-title mb-0 flex-grow-1 fs-14">Servis Listesi</h4>
          <div className="flex-shrink-0"></div>
        </CardHeader>
        <CardBody>
          <Row className="mb-3">
            <CardBody className="border border-dashed border-end-0 border-start-0 border-top-0">
              <form>
                <div className="d-flex flex-wrap justify-content-between">
                  <div className="d-flex flex-wrap" style={{ gap: "10px" }}>
                    <div style={{ width: "100px" }}>
                      <select
                        className="form-select mb-3"
                        aria-label="Sayfa başına kayıt sayısı"
                        value={getState().pagination.pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                      >
                        {pageSizeOptions.map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <div
                      className="search-box me-2 mb-2 d-inline-block border"
                      style={{ borderRadius: "0.25em" }}
                    >
                      <DebouncedInput
                        value={globalFilter ?? ""}
                        onChange={(value) => setGlobalFilter(value)}
                        placeholder={"Ara..."}
                      />
                      <i className="bx bx-search-alt search-icon"></i>
                    </div>
                  </div>
                </div>
              </form>
              <Row>
                <Col sm={6} md={4} lg={3} xl={2}>
                  <label className="form-label">Durum</label>
                  <select
                    className="form-select mb-3"
                    aria-label="yufus"
                    value={filterStatus}
                    onChange={(e) => {
                      setFilterStatus(e.target.value);
                    }}
                  >
                    <option value="">Tümü</option>
                    <option value={true}>Aktif</option>
                    <option value={false}>Pasif</option>
                  </select>
                </Col>
              </Row>
            </CardBody>
          </Row>

          <div
            style={{
              overflowX: "auto",
            }}
          >
            <Table className="align-middle">
              <thead>
                {getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="text-muted"
                        style={{
                          width: header.column.columnDef.size,
                          minWidth: header.column.columnDef.minSize,
                          maxWidth: header.column.columnDef.maxSize,
                          letterSpacing: "0.8px",
                        }}
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              cursor: "pointer",
                              userSelect: "none",
                            }}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: <i className="ri-arrow-down-s-line"></i>,
                              desc: <i className="ri-arrow-up-s-line"></i>,
                            }[header.column.getIsSorted()] ?? null}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody>
                {ordersLoading ? (
                  <tr>
                    <td colSpan="100%" className="text-center pt-5 pb-5">
                      <Spinner color="primary" />
                      <p className="text-dark">Yükleniyor...</p>
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td
                      colSpan="100%"
                      className="text-center pt-5 pb-5 text-muted"
                    >
                      <h1>
                        <i className="ri-error-warning-line text-muted"></i>
                      </h1>
                      <p>Kayıt bulunamadı!</p>
                    </td>
                  </tr>
                ) : (
                  getRowModel().rows.map((row, index) => {
                    return (
                      <tr key={row.id}>
                        {row.getVisibleCells().map((cell) => {
                          return (
                            <td
                              key={cell.id}
                              style={{
                                width: cell.column.columnDef.size,
                                minWidth: cell.column.columnDef.minSize,
                                maxWidth: cell.column.columnDef.maxSize,
                              }}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>

          <TablePagination table={table} />
        </CardBody>
      </Card>

      {selectedRowData && (
        <>
          <UpdateModal
            id={selectedRowData.id}
            isOpen={modalState.update}
            toggle={() => toggleModal("update")}
          />
          <DeleteModal
            id={selectedRowData.id}
            isOpen={modalState.delete}
            toggle={() => toggleModal("delete")}
          />
        </>
      )}
    </Fragment>
  );
};

export default OrderTable;
